/**
 * @Time    : 2024/1/12 15:42
 * @Author  : tlovet
 * @Software: WebStorm
 */

import defaultAvatar from '@/assets/avatar.png';
import logo from '@/assets/logo.png';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  FullScreen,
  Question,
  SelectLang,
  ThemeSwitch,
} from '@/components/CustomLayout';
import UnAccessiblePage from '@/pages/403';
import { getCurrentUser } from '@/services/auth';
import { getTheme } from '@/utils/localStorage';
import { getAccessToken, redirectLogin } from '@/utils/login';
import { requestConfig } from '@/utils/request';
import { watermark } from '@/utils/watermark';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { PageLoading } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { FloatButton } from 'antd';
import { parse } from 'query-string';
import defaultSettings from '../config/defaultSettings';

// 从浏览器localStorage里面获取系统主题，会覆盖defaultSettings里面的配置
const navTheme: NavThemeType = getTheme();

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 * axios配置：https://blog.csdn.net/weixin_67917191/article/details/134929983
 */
export const request: RequestConfig = requestConfig;

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: AUTH.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<AUTH.CurrentUser | undefined>;
}> {
  // 异步获取当前登陆用户信息，请求异常时跳转到登陆界面
  const fetchUserInfo = async () => {
    try {
      const response = await getCurrentUser();
      return response.data;
    } catch (error) {
      redirectLogin();
    }
    return undefined;
  };

  // 如果不是登陆回调地址，执行
  // 先从cookie中获取登陆的Token
  const accessToken = getAccessToken();
  if (!accessToken) {
    redirectLogin();
    // 此处一定要有return
    return {
      fetchUserInfo,
      settings: { ...defaultSettings, navTheme } as Partial<LayoutSettings>,
    };
  } else {
    // 重定向到登陆
    let redirectUrl: string = location.pathname;
    if (history.location.pathname === LOGIN_CALLBACK_PATH) {
      const { redirect = '/' } = parse(history?.location?.search);
      redirectUrl = redirect as string;
    }
    // 一定要带上search，要不然刷新页面，参数会丢失
    history.push({
      pathname: redirectUrl,
      search: history.location.search,
    });
  }
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser: currentUser || {},
    settings: { ...defaultSettings, navTheme } as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
// @ts-ignore
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // 自定义水印，必须在layout中指定className=custom-watermark
  // 不使用Layout自带的waterMarkProps，自带的水印可以删除
  const nowDate = new Date().toLocaleDateString();
  watermark({
    content: [`${initialState?.currentUser?.username} ${initialState?.currentUser?.name}`, nowDate],
    container: document.querySelector('.custom-watermark'),
  });
  return {
    // 放在最前面，可以用下面的配置进行覆盖
    ...initialState?.settings,
    // 系统标题
    title: SYSTEM_NAME,
    // 菜单的宽度
    siderWidth: 256,
    // 指定classname，水印使用
    className: 'custom-watermark',
    // logo默认从config/defaultSettings中加载，也可以在此指定
    logo: logo,
    actionsRender: (props) => {
      if (props.isMobile) return [<Question key="doc" />, <SelectLang key="selectLang" />];
      if (typeof window === 'undefined') return [];
      return [
        // props.layout !== 'side' && document.body.clientWidth > 1400 ? <SearchInput /> : undefined,
        // <GlobalSearch key="search" />,
        <Question key="doc" />,
        <SelectLang key="selectLang" />,
        <FullScreen key="fullScreen" />,
        <ThemeSwitch key="themeSwitch" />,
      ];
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar || defaultAvatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: [
    //     `${initialState?.currentUser?.username} ${initialState?.currentUser?.name}`,
    //     new Date().toLocaleDateString(),
    //   ],
    //   color: 'rgba(0, 0, 0, 0.15)',
    //   fontSize: 14,
    //   zIndex: 10000,
    //   rotate: -20,
    //   gap: [100, 100],
    //   offset: undefined,
    // },
    footerRender: () => <Footer />,
    onPageChange: async () => {
      const accessToken = getAccessToken();
      // 如果cookie中登陆的Token不存在，重定向到登陆界面
      // 因获取当前登陆用户信息时异步，此处不能通过!initialState?.currentUser来判断是否登陆
      if (!accessToken) {
        redirectLogin();
      }

      // 如果没有登录，重定向到 login
      if (
        (!initialState?.currentUser || !accessToken) &&
        !LOGIN_WHITE_LIST_PATH.includes(location.pathname)
        // location.pathname !== LOGIN_CALLBACK_PATH
      ) {
        history.push(LOGIN_CALLBACK_PATH);
      }

      // login页面，不需要获取当前登陆用户信息
      // if (location.pathname !== LOGIN_CALLBACK_PATH) {
      if (!LOGIN_WHITE_LIST_PATH.includes(location.pathname)) {
        // 页面跳转都需要获取当前登陆用户信息，判断用户是否退出
        const currentUser = await initialState?.fetchUserInfo?.();
        await setInitialState((preInitialState: any) => ({
          ...preInitialState,
          currentUser: currentUser || {},
        }));
      }
    },
    links: [],
    appList: [
      // {
      //   icon: 'https://gw.alipayobjects.com/zos/rmsportal/KDpgvguMpGfqaHPjicRK.svg',
      //   title: 'Ant Design',
      //   desc: '杭州市较知名的 UI 设计语言',
      //   url: 'https://ant.design',
      // },
    ],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    unAccessible: <UnAccessiblePage />,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <FloatButton.BackTop />
        </>
      );
    },
  };
};
